
import React from 'react';
import { BotCard } from '@/components/dashboard/BotCard';
import { BotListItem } from './BotListItem';
import { Skeleton } from '@/components/ui/skeleton';
import { Card } from '@/components/ui/card';
import { Bot, BotStatus, ExchangePlatform } from '@/types/trader';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { useBotDeploymentValidation } from '@/hooks/use-bot-deployment-validation';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

type ViewMode = 'grid' | 'list';

interface BotListProps {
  bots: Bot[] | undefined;
  filteredBots: Bot[];
  isLoading: boolean;
  searchQuery: string;
  viewMode: ViewMode;
  onViewDetails: (botId: string) => void;
  onToggleStatus: (botId: string, currentStatus: BotStatus) => void;
  onDeleteClick: (bot: Bot) => void;
  onOpenDeployDialog: () => void;
}

export const BotList = ({
  bots,
  filteredBots,
  isLoading,
  searchQuery,
  viewMode,
  onViewDetails,
  onToggleStatus,
  onDeleteClick,
  onOpenDeployDialog
}: BotListProps) => {
  const { canDeployBot, getDeploymentErrorMessage } = useBotDeploymentValidation();
  const { toast } = useToast();
  const { t } = useTranslation();

  // Helper function to ensure exchange is always an array
  const ensureExchangeArray = (exchange: string | string[] | ExchangePlatform[] | undefined): ExchangePlatform[] => {
    if (!exchange) return [];
    if (Array.isArray(exchange)) return exchange as ExchangePlatform[];
    return [exchange as ExchangePlatform];
  };

  const handleDeployClick = () => {
    if (!canDeployBot()) {
      toast({
        title: t('deployment_limit_reached'),
        description: getDeploymentErrorMessage(),
        variant: "destructive"
      });
      return;
    }
    onOpenDeployDialog();
  };

  if (isLoading) {
    if (viewMode === 'list') {
      return (
        <div className="space-y-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="p-4 rounded-lg bg-card border space-y-3">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-5 w-1/3" />
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-4 w-1/4" />
                </div>
                <div className="flex gap-2">
                  <Skeleton className="h-8 w-20" />
                  <Skeleton className="h-8 w-20" />
                  <Skeleton className="h-8 w-20" />
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3].map(i => (
          <Card key={i} className="p-5 space-y-4">
            <div className="flex justify-between">
              <Skeleton className="h-6 w-1/3" />
              <Skeleton className="h-6 w-1/4" />
            </div>
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-8 w-1/2" />
            <div className="flex justify-between">
              <Skeleton className="h-8 w-1/3" />
              <Skeleton className="h-8 w-1/4" />
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (filteredBots.length > 0) {
    if (viewMode === 'list') {
      return (
        <div className="space-y-3">
          {filteredBots.map((bot) => (
            <BotListItem
              key={bot.id}
              bot={bot}
              onViewDetails={onViewDetails}
              onToggleStatus={onToggleStatus}
              onDelete={onDeleteClick}
            />
          ))}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredBots.map((bot) => (
          <BotCard
            key={bot.id}
            id={bot.id}
            name={bot.name}
            type={bot.type}
            status={bot.status}
            description={bot.description}
            connectedAccounts={bot.connected_accounts_count || 0}
            colorScheme={bot.color_scheme}
            exchange={ensureExchangeArray(bot.exchange)}
            onViewDetails={onViewDetails}
            onToggleStatus={onToggleStatus}
            onDelete={() => onDeleteClick(bot)}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="bg-card p-6 rounded-lg text-center max-w-md">
        <h3 className="text-lg font-medium mb-2">{t('no_bots_found')}</h3>
        <p className="text-muted-foreground mb-4">
          {searchQuery
            ? t('no_bots_search_result', { searchQuery })
            : t('no_bots_deployed')}
        </p>
        {!searchQuery && (
          <Button
            className="bg-primary hover:bg-primary/90"
            onClick={handleDeployClick}
            disabled={!canDeployBot()}
          >
            <Plus size={16} className="mr-2" />
            {t('deploy_new_bot')}
          </Button>
        )}
      </div>
    </div>
  );
};
